import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import useAuth from './useAuth';

type UserInfo = {
    username: string;
    isAdmin: boolean;
};

type UserInfoResponse = {
    username: string;
    is_admin: boolean;
};

type UserInfoContextValue = {
    userInfo: UserInfo | null;
    loadingUserInfo: boolean;
    loadUserInfo: () => Promise<void>;
    logout: () => void;
};

type UserInfoProviderProps = {
    children: ReactNode;
};

const UserInfoContext = createContext<UserInfoContextValue | null>(
    null,
);

function UserInfoProvider({ children }: UserInfoProviderProps) {
    const { apiRequest, isLoggedIn, logout } = useAuth();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loadingUserInfo, setLoadingUserInfo] = useState(false);

    const loadUserInfo = useCallback(async () => {
        if (!isLoggedIn || loadingUserInfo) {
            return;
        }

        setLoadingUserInfo(true);

        try {
            const data = await apiRequest<UserInfoResponse>(
                '/api/users/info',
            );

            setUserInfo({
                username: data.username,
                isAdmin: data.is_admin,
            });
        } catch (error: unknown) {
            console.error('Failed to load user info.', error);
            setUserInfo(null);
            logout();
        } finally {
            setLoadingUserInfo(false);
        }
    }, [apiRequest, isLoggedIn, loadingUserInfo, logout]);

    const logoutUser = useCallback(() => {
        setUserInfo(null);
        logout();
    }, [logout]);

    useEffect(() => {
        if (isLoggedIn && !userInfo && !loadingUserInfo) {
            void loadUserInfo();
        }

        if (!isLoggedIn && userInfo) {
            setUserInfo(null);
        }
    }, [
        isLoggedIn,
        loadingUserInfo,
        loadUserInfo,
        userInfo,
    ]);

    const value = useMemo<UserInfoContextValue>(() => ({
        userInfo,
        loadingUserInfo,
        loadUserInfo,
        logout: logoutUser,
    }), [userInfo, loadingUserInfo, loadUserInfo, logoutUser]);

    return (
        <UserInfoContext.Provider value={value}>
            {children}
        </UserInfoContext.Provider>
    );
}

function useUserInfo(): UserInfoContextValue {
    const context = useContext(UserInfoContext);

    if (!context) {
        throw new Error(
            'useUserInfo must be used inside a UserInfoProvider.',
        );
    }

    return context;
}

export { UserInfoProvider, useUserInfo };
export type { UserInfo, UserInfoContextValue };
export default useUserInfo;
